import csatKeys from '@/constants/configuration/csat-keys.json';
import tfaStatuses from '@/constants/tfa-status';
import API_AUTH_LOGIN from '@/services/api/authLogin.js';
import API_CONFIG from '@/services/api/config.js';
import API_DEX from "@/services/api/dex.js";
import API_LOGIN from '@/services/api/login.js';
import API_LOGOUT from '@/services/api/logout';
import API_PROFILE from '@/services/api/profile.js';
import API_TFA from '@/services/api/twoFactorAuthenticatication.js';
import API_RENEW_TOKEN from '@/services/api/renewToken.js';
import { getEnvAndDex } from '@/utils/get-env-and-dex';
import { portalLoginPath } from '@/utils/portal-login-path';
import axios from 'axios';
import moment from 'moment';

const { env } = getEnvAndDex();

const SHELL_HOSTED = process.env.REACT_APP_SHELL_HOSTED === 'true';

function handleTfa(user) {
	const { tfaRequired, tfaUserSetup, tfaVerified } = user

	let tfaStatus = tfaStatuses.none

	if (user?.isAdmin) {
		return tfaStatus = tfaStatuses.none
	}

	if (tfaVerified) {
		return tfaStatus = tfaStatuses.none
	}

	if (tfaUserSetup) {
		return tfaStatus = tfaStatuses.otp
	}

	if (tfaRequired) {
		return tfaStatus = tfaStatuses.setUp
	}

	return tfaStatus = tfaStatuses.none
}

async function handleUser(response) {
	const { user } = response

	let [profile, type] = [[], "participant"]

	if ((!user?.tfaRequired) || (user?.tfaRequired && user?.tfaVerified)) {
		profile = (await API_PROFILE.index())[1]

		if (profile?.organization?.status !== "APPROVED") {
			type = "nonParticipant"
		}
	}

	if (user?.isAdmin) {
		type = "admin"
	}

	return {
		type,
		profile
	}
}

function syncProfile() {
	return async (dispatch) => {
		const profile = (await API_PROFILE.index())[1]

		dispatch({
			type: 'auth/syncProfile',
			payload: profile
		})

		sessionStorage.setItem('profile', JSON.stringify(profile))
	}
}

// Shell-hosted bootstrap — replicates the post-token side of login() so the
// admin-ui pages can actually render their content. Without this, profile,
// environments, features, userType all stay null and the in-app hooks bail.
function hydrateShellSession() {
	return async (dispatch) => {
		const token = sessionStorage.getItem('adminCoreToken');
		if (!token) return;
		axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

		// Refresh + grab user.
		let user = null;
		try {
			const userStr = sessionStorage.getItem('user');
			if (userStr) user = JSON.parse(userStr);
		} catch (_) { user = null; }
		try {
			const [renewErr, renewRes] = await API_RENEW_TOKEN.create(null, {
				headers: { Authorization: `Bearer ${token}` }
			});
			if (!renewErr && renewRes) {
				const renewedToken = renewRes.token || token;
				user = renewRes.user || user;
				axios.defaults.headers.common['Authorization'] = `Bearer ${renewedToken}`;
				sessionStorage.setItem('adminCoreToken', renewedToken);
				if (user) sessionStorage.setItem('user', JSON.stringify(user));
			}
		} catch (_) { /* fall through with cached user */ }

		if (!user) return;

		// Features and profile come from sessionStorage (portal pre-fetched at login).
		// shouldHydrate in src/index.js redirects to /login if either is missing.
		const featuresStr = sessionStorage.getItem('features');
		const features = featuresStr ? JSON.parse(featuresStr) : [];

		let environments = null;
		try {
			environments = (await API_CONFIG.index())[1];
		} catch (_) { /* leave null */ }

		let profile = [];
		let type = 'participant';
		try {
			if ((!user?.tfaRequired) || (user?.tfaRequired && user?.tfaVerified)) {
				const profileStr = sessionStorage.getItem('profile');
				profile = profileStr ? JSON.parse(profileStr) : [];
				if (profile?.organization?.status !== 'APPROVED') {
					type = 'nonParticipant';
				}
			}
			if (user?.isAdmin) type = 'admin';
		} catch (_) { /* keep defaults */ }

		const tfaValue = handleTfa(user);

		sessionStorage.setItem('environments', JSON.stringify(environments));
		sessionStorage.setItem('userType', type);
		sessionStorage.setItem('profile', JSON.stringify(profile));
		sessionStorage.setItem('lastTimeStamp', moment());
		sessionStorage.setItem('tfa', tfaValue);
		sessionStorage.setItem('features', JSON.stringify(features));
		// Tag which app last hydrated, so cross-app hops re-hydrate. See index.js shouldHydrate.
		sessionStorage.setItem('profileSurface', 'admin');

		dispatch({
			type: 'auth/login',
			payload: { token: sessionStorage.getItem('adminCoreToken'), user },
			environments,
			userType: type,
			profile,
			tfa: tfaValue,
			features
		});
	};
}

function login(goTo, credentials, flow, returnUrl) {
	return async (dispatch) => {
		let error, response

		switch (flow) {
			case "setUpTfa":
				[error, response] = await API_TFA.verifyTfa({ ...credentials })
				break;
			case "otpVerify":
				[error, response] = await API_TFA.validateTfa({ ...credentials })

				break;
			case "authLogin":
				[error, response] = await API_AUTH_LOGIN.create({ ...credentials })
				break;
			default:
				[error, response] = await API_LOGIN.create({ ...credentials, email: credentials.email.toLowerCase() })
				break;
		}

		const [dexError, dexResponse] = await API_DEX.index();

		const { features } = dexResponse;
		const featuresList = features || [];

		if(dexError) {
			throw new Error("Failed to load dex config");
		}

		if (error) {
			if (error.message === 'Invalid credentials.' || error.message === 'Failed to Fetch User Details' || error.message === 'Unable to retrieve user from DB') {
				throw new Error('credentialError')
			}

			if (String(error.message.toLowerCase()).includes("status code")) {
				throw new Error("An error occured! Please contact administrator.")
			}
			throw new Error(error.message)
		}

		const { token, user } = response;

		axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

		const responseConfig = (await API_CONFIG.index())[1]

		const { type, profile } = await handleUser(response)

		const tfaValue = handleTfa(user)



		dispatch({
			type: 'auth/login',
			payload: response,
			environments: responseConfig,
			userType: type,
			profile: profile,
			tfa: tfaValue,
			features: featuresList
		})

		sessionStorage.setItem('adminCoreToken', token)
		sessionStorage.setItem('user', JSON.stringify(user))
		sessionStorage.setItem('environments', JSON.stringify(responseConfig))
		sessionStorage.setItem('userType', type)
		sessionStorage.setItem('profile', JSON.stringify(profile))
		sessionStorage.setItem('lastTimeStamp', moment())
		sessionStorage.setItem('tfa', tfaValue)
		sessionStorage.setItem('features', JSON.stringify(featuresList));
		goTo(returnUrl ? decodeURIComponent(returnUrl) : `/`)
	}
}

// Drop the local session: token store, axios auth header, and the CSAT-related
// localStorage keys. Shared by the shell and legacy logout paths.
function clearLocalSession() {
	sessionStorage.clear();
	delete axios.defaults.headers.common['Authorization'];
	localStorage.removeItem('userEmail');
	localStorage.removeItem('lockedTime');
	localStorage.removeItem('lockedAccount');
}

// Remove the injected CSAT widget script, if present.
function removeCsatWidget() {
	const csatWidgetBaseUrl = csatKeys[env]?.CSAT_WIDGET_BASE_URL;
	const csatSpaceKey = csatKeys[env]?.CSAT_SPACE_KEY;
	const selector = `script[src="${csatWidgetBaseUrl}${csatSpaceKey}?onload=onUserSnapLoad"]`;
	if (document.querySelector(selector)) {
		document.querySelector(selector).remove();
	}
}

// Best-effort server-side logout for BOTH surfaces (a single shell tab holds both
// tokens). Fired keepalive + fire-and-forget so the requests survive the full-page
// redirect without blocking on slow in-flight page requests. Only flips each
// token's `revoked` flag. Read tokens before clearLocalSession() wipes them.
function postLogoutKeepalive() {
	const adminToken = sessionStorage.getItem('adminCoreToken');
	const pitstopToken = sessionStorage.getItem('pitstopCoreToken');
	const pitstopHostname = sessionStorage.getItem('pitstopHostname');
	const dex = sessionStorage.getItem('selectedDex');
	if (adminToken) {
		fetch('/api/v1/logout', {
			method: 'POST',
			keepalive: true,
			headers: { Authorization: `Bearer ${adminToken}`, 'X-Request-From': 'admin', ...(dex ? { dex } : {}) },
		}).catch(() => {});
	}
	if (pitstopToken) {
		fetch('/api/logout', {
			method: 'POST',
			keepalive: true,
			headers: {
				Authorization: `Bearer ${pitstopToken}`,
				'X-Request-From': 'pitstop',
				// pitstop-core only validates the token when Channel is UI; without it the logout 401s.
				Channel: 'UI',
				...(pitstopHostname ? { 'X-Pitstop-Hostname': pitstopHostname } : {}),
			},
		}).catch(() => {});
	}
}

function logout(goTo) {
	return async (dispatch) => {
		// Unified portal: fire both surfaces' /logout (keepalive), wipe the whole
		// local session, and full-page redirect to the user's own dex login page
		// (portal.../<dex>; selection page when unknown). No auth/logout
		// dispatch — the full-page nav destroys the redux store anyway, and skipping
		// it avoids re-running the route guards on a null user mid-teardown.
		if (SHELL_HOSTED) {
			const target = portalLoginPath(); // read BEFORE clearLocalSession() wipes selectedDex
			postLogoutKeepalive();
			clearLocalSession();
			// admin-ui-shell + pitstop-ui-shell share one origin, so clear pitstop's
			// client caches too (no-op if the DB was never created / key absent).
			try {
				indexedDB.deleteDatabase('ttDataDB');
				indexedDB.deleteDatabase('ttFileDB');
			} catch (e) { /* best-effort */ }
			localStorage.removeItem('isTableView');
			localStorage.removeItem('isListView');
			removeCsatWidget();
			window.location.assign(target);
			return;
		}

		// Legacy (standalone): await own-surface logout, reset redux, then in-app
		// navigate + reload. Unchanged behavior.
		await API_LOGOUT.create();
		dispatch({ type: 'auth/logout' });
		clearLocalSession();
		removeCsatWidget();
		goTo('/login');
		window.location.reload();
	}
}

function renewToken() {
	return async (dispatch) => {
		const currentToken = sessionStorage.getItem("adminCoreToken");
			const [error, response] = await API_RENEW_TOKEN.create(null, {
				headers: {
					Authorization: `Bearer ${currentToken}`,
				},
			});
			if (error) throw new Error(error.message);
			const { token, user } = response;
			axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
			sessionStorage.setItem("adminCoreToken", token);
			sessionStorage.setItem("user", JSON.stringify(user));
			sessionStorage.setItem("lastTimeStamp", moment());
			dispatch({
				type: "auth/renewToken",
				payload: { token, user },
			});
	};
}

export {
	login,
	logout,
	syncProfile,
	renewToken,
	hydrateShellSession
};

